// Operator › Clients (SUPER-ADMIN): the platform operator workspace's client list. A super admin
// sees every client the platform agency operates; an operator only the clients assigned to them.
// Each row: onboarding status, the client's Skillsmith (its private space, existing page), and
// "Open" — the caller's operator access there (audited), then the client's own app. Fleet-level
// surfaces (Agent health, deploys, the week roll-up) are linked from the header. Every load of
// this list is an audit row (`operator_workspace.clients_listed`).
import {
  Badge,
  Button,
  EmptyState,
  LocationHeader,
  Notice,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { BuildingIcon } from "lucide-react";
import type { Api } from "../../api/client";
import { LinkButton, OpsBody, OpsFrame, operatorCrumb, RowSub, TABLE_ROW_HOVER } from "../ops-ux";
import { LoadError, Loading } from "../screen-parts";

const STATUS: Record<string, { label: string; variant: "success" | "warning" | "neutral" }> = {
  live: { label: "Live", variant: "success" },
  setting_up: { label: "Setting up", variant: "warning" },
  awaiting_owner: { label: "Awaiting owner", variant: "neutral" },
};

type CaseSummary = NonNullable<
  Awaited<
    ReturnType<Api["client"]["operator"]["workspace"]["clients"]["list"]>
  >["data"][number]["onboarding"]["case"]
>;

const FOLLOWUP: Record<string, string> = {
  reminder: "reminder",
  missing_list: "what's missing",
  call_offer: "call offer",
  operator_alert: "your follow-up",
};

/** ONBOARDING-P1: the client's onboarding case under its status (CLIENT_ONBOARDING). */
function CaseLine({ c }: { c: CaseSummary }) {
  const due = new Date(c.due_at).toLocaleDateString();
  const intake =
    c.status === "intake_complete"
      ? "Intake complete"
      : `Intake ${c.percent}% · ${c.missing} missing · due ${due}`;
  return (
    <RowSub data-slot="onboarding-case">
      Plan {c.plan_key} · {intake}
      {c.link_delivery !== "sent" && c.status === "link_sent"
        ? " · link not mailed: send it to the owner"
        : ""}
      {c.operator_alerted ? " · needs your call" : ""}
      {c.overdue ? " · overdue" : ""}
      {c.next_followup
        ? ` · next: ${FOLLOWUP[c.next_followup.kind] ?? c.next_followup.kind} ${new Date(c.next_followup.due_at).toLocaleDateString()}`
        : ""}
    </RowSub>
  );
}

export function WorkspaceClientsPage({ api }: { api: Api }) {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["operator", "workspace", "clients"],
    queryFn: () => api.client.operator.workspace.clients.list(),
  });
  const open = useMutation({
    mutationFn: (companyId: string) =>
      api.client.operator.workspace.clients.open({ params: { company_id: companyId }, body: {} }),
    onSuccess: (r) => void navigate({ to: "/c/$slug", params: { slug: r.company.slug } }),
  });
  const rows = q.data?.data ?? [];
  const superAdmin = q.data?.role === "super_admin";
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[operatorCrumb, { label: "Clients" }]}
        icon={BuildingIcon}
        title={superAdmin ? "All clients" : "My clients"}
        count={q.isPending ? undefined : rows.length}
        actions={
          <LinkButton render={<Link to="/operator/onboarding" />}>Onboarding cases</LinkButton>
        }
      />
      <OpsBody>
        {open.isError ? (
          <Notice tone="danger" role="alert">
            The client could not be opened. It may no longer be assigned to you.
          </Notice>
        ) : null}
        {q.isPending ? (
          <Loading label="Loading clients" />
        ) : q.isError ? (
          <LoadError what="Clients" error={q.error} />
        ) : rows.length === 0 ? (
          <EmptyState
            variant="card"
            icon={BuildingIcon}
            title="No clients yet"
            description={
              superAdmin
                ? "A client appears here once the platform operates it (Operator › Companies › New company)."
                : "A super admin assigns clients to you; they appear here."
            }
          />
        ) : (
          <Table data-slot="operator-workspace-clients">
            <TableCaption>
              {superAdmin
                ? "Every client the platform operates. Opening one is recorded."
                : "The clients assigned to you. Opening one is recorded."}
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Client</TableHead>
                <TableHead scope="col">Onboarding</TableHead>
                <TableHead scope="col">Skillsmith</TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const s = STATUS[r.onboarding.status] ?? STATUS.awaiting_owner;
                return (
                  <TableRow
                    key={r.company.id}
                    data-client={r.company.id}
                    className={TABLE_ROW_HOVER}
                  >
                    <TableHead scope="row" className="font-normal">
                      <span className="font-medium">{r.company.name}</span>
                      <RowSub>
                        {r.onboarding.de_active} active DE · {r.onboarding.de_onboarding} onboarding
                        {r.onboarding.pending_invitations > 0
                          ? ` · ${r.onboarding.pending_invitations} pending invitation(s)`
                          : ""}
                      </RowSub>
                    </TableHead>
                    <TableCell>
                      <Badge variant={s?.variant ?? "neutral"}>{s?.label}</Badge>
                      {r.onboarding.case ? (
                        <>
                          <CaseLine c={r.onboarding.case} />
                          <Link
                            to="/operator/onboarding/$companyId"
                            params={{ companyId: r.company.id }}
                            className="text-xs underline-offset-4 hover:underline"
                          >
                            Open the onboarding case
                          </Link>
                        </>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {r.skillsmith && r.opened ? (
                        <Link
                          to="/operator/system-agents/$companyId/$membershipId"
                          params={{
                            companyId: r.company.id,
                            membershipId: r.skillsmith.membership_id,
                          }}
                          className="underline-offset-4 hover:underline"
                        >
                          {r.skillsmith.name}
                        </Link>
                      ) : r.skillsmith ? (
                        <span className="text-muted-foreground">
                          {r.skillsmith.name} (open the client first)
                        </span>
                      ) : (
                        <span className="text-muted-foreground">None</span>
                      )}
                      {/* SKILLSMITH-CONTROL: give this client's Skillsmith orders (super admins). */}
                      {superAdmin ? (
                        <RowSub>
                          <Link
                            to="/operator/skillsmith/$companyId"
                            params={{ companyId: r.company.id }}
                            className="underline-offset-4 hover:underline"
                          >
                            Give orders
                          </Link>
                        </RowSub>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={open.isPending}
                        onClick={() => open.mutate(r.company.id)}
                      >
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </OpsBody>
    </OpsFrame>
  );
}
